import { useMemo } from 'react'

import { encodeQr } from './qrEncode'

/**
 * The upload link as a QR code.
 *
 * Drawn as one `<path>` of module-sized squares rather than a `<rect>` each:
 * a version-6 code is 1681 modules and half of them dark, and 800 DOM nodes
 * in a dialog is a visible pause on a phone.
 *
 * The quiet zone is part of the picture, not padding on the container — a
 * code flush against a coloured tile is one many scanners will not read. Four
 * modules is what the specification asks for.
 */
export function QrCode({
  value,
  label,
  className,
}: {
  value: string
  /** The accessible name. A QR code is an image of a link, and a reader that
   *  announces "Grafik" alone has said nothing useful. */
  label: string
  className?: string
}) {
  const drawing = useMemo(() => {
    try {
      const { modules, size } = encodeQr(value)
      const quiet = 4
      let path = ''
      for (let row = 0; row < size; row += 1) {
        for (let col = 0; col < size; col += 1) {
          if (modules[row][col]) path += `M${col + quiet} ${row + quiet}h1v1h-1z`
        }
      }
      return { path, extent: size + quiet * 2 }
    } catch {
      // Too long for this encoder — the link is rendered as text either way.
      return null
    }
  }, [value])

  if (!drawing) return null

  return (
    <svg
      viewBox={`0 0 ${drawing.extent} ${drawing.extent}`}
      role="img"
      aria-label={label}
      shapeRendering="crispEdges"
      className={className}
    >
      <rect width={drawing.extent} height={drawing.extent} fill="#ffffff" />
      <path d={drawing.path} fill="#2a6ae6" />
    </svg>
  )
}
